import type { CloudResult } from '../../http/errors.js';

export type ResolvedAuthIdentifier =
    | { kind: 'email'; value: string; username: string }
    | { kind: 'phone'; value: string; username: string };

/**
 * 认证入口只接受一个账号标识。公开类型负责拦住正常的 TS 调用，这里继续防御
 * JavaScript、`as any` 和对象展开带来的双标识输入，禁止静默偏向任一渠道。
 */
export function resolveAuthIdentifier(
    credentials: { email?: string; phone?: string },
    operation: string,
): CloudResult<ResolvedAuthIdentifier> {
    const email = credentials.email ?? '';
    const phone = credentials.phone ?? '';
    if (Boolean(email) === Boolean(phone)) {
        return {
            data: null,
            error: {
                kind: 'invalid-request',
                message: `${operation} requires exactly one of email or phone`,
                status: 0,
            },
        };
    }
    return {
        data: email
            ? { kind: 'email', value: email, username: email }
            : { kind: 'phone', value: phone, username: normalizePhone(phone) },
        error: null,
    };
}

/**
 * 归一化国内手机号为 `+86 13800000000` 形式（+86 后带一个空格）。
 *
 * 兼容输入：裸号（`13800000000`）、带区号但无空格（`+8613800000000`、
 * `8613800000000`）、国际冠码（`0086...`）、各种分隔符（空格/连字符）。
 * 非 11 位国内手机号（如港澳号 `+852...`）原样返回，不破坏非大陆号码。
 */
export function normalizePhone(phone: string): string {
    const digits = phone.replace(/\D/g, '');
    let local = digits;
    if (local.startsWith('0086')) {
        local = local.slice(4);
    } else if (local.startsWith('86') && local.length === 13) {
        local = local.slice(2);
    }
    return /^1\d{10}$/.test(local) ? `+86 ${local}` : phone;
}
